import { useQuery } from '@parkchirp/api-client';
import { FacilityQueryContextEnum } from 'parkchirp-shared/enums';
import { useMemo, useState } from 'react';
import { useDebounce } from '../../hooks';
import { GET_FACILITIES_V2 } from '../../queries';
import type { FacilityV2 } from '../../types';
import { ReportingMultiSelect } from './ReportingMultiSelect';

export type FacilityOption = {
	id: string;
	label: string;
};

type FacilitiesResponse = {
	listFacilities: FacilityV2[];
};

type Filter = {
	op: string;
	propertyName: string;
	value: boolean | string;
};

type Props = {
	organizationId?: number | null;
	value: FacilityOption[];
	onChange: (value: FacilityOption[]) => void;
};

export const FacilityFilter = ({ organizationId, value, onChange }: Props) => {
	const [options, setOptions] = useState<FacilityOption[]>([]);
	const [searchTerm, setSearchTerm] = useState('');

	const updateSearchTerm = useDebounce(setSearchTerm, 800);

	const filters = useMemo(() => {
		const queryFilters: Filter[] = [
			{
				propertyName: 'facilityName',
				op: 'contains',
				value: searchTerm,
			},
			{
				propertyName: 'facilityNumber',
				op: 'contains',
				value: searchTerm,
			},
		];

		if (organizationId && organizationId !== 0) {
			queryFilters.push({
				propertyName: 'organizationId',
				op: 'equals',
				value: `${organizationId}`,
			});
		}

		return queryFilters;
	}, [organizationId, searchTerm]);

	const { loading } = useQuery<FacilitiesResponse>(GET_FACILITIES_V2, {
		variables: {
			offset: 0,
			limit: 10,
			queryContext: FacilityQueryContextEnum.AUTOCOMPLETE,
			filters,
			order: {
				field: 'facilityName',
				sort: 'asc',
			},
		},
		skip: !searchTerm,
		onCompleted: ({ listFacilities: facilities }) => {
			setOptions(
				facilities.map((facility) => ({
					id: facility.id,
					label: `${facility.facilityName} (${facility.facilityNumber})`,
				})),
			);
		},
		context: {
			apiVersion: 'v2',
			appName: process.env.NEXT_PUBLIC_APP_NAME,
		},
	});

	return (
		<ReportingMultiSelect
			label='Facilities'
			loading={loading}
			options={options}
			value={value}
			getOptionId={(option) => option.id}
			getOptionLabel={(option) => option.label}
			onInputChange={updateSearchTerm}
			onChange={onChange}
		/>
	);
};
